import ReactDOM from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import useAuth from 'hooks/UseAuth';
import { useOutside } from 'hooks/UseOutside';

import { toast } from 'react-toastify';
import { RadialSelectBox } from 'shared/components/molecules/RadialSelectBox/RadialSelectBox';

import { HandleInputChange } from './utils/HandleInputChange';
import api from 'services/api';

import {
  IBankDataModal,
  IBankFormData,
  IOptions,
  IResponse,
} from './interfaces';

import {
  ModalButton,
  ModalCloseButton,
  ModalContainer,
  ModalInput,
  ModalSection,
  ModalTitle,
  SelectWrapper,
} from './_bankDataModal';

export const BankDataModal = ({
  toggleModal,
  state,
  setState,
  callback,
}: IBankDataModal) => {
  const modalRoot = document.getElementById('root-modal');
  const modalRef = useRef(null);

  const { user } = useAuth();

  const [bankName, setBankName] = useState({
    value: '',
    label: '',
  });
  const [bankNameOptions, setBankNameOptions] = useState([] as Array<IOptions>);

  const [accountType, setAccountType] = useState({
    value: '',
    label: '',
  });
  const [accountTypeOptions, setAccountTypeOptions] = useState(
    [] as Array<IOptions>,
  );

  const [bankFormData, setBankFormData] = useState({} as IBankFormData);

  const [disableButton, setDisableButton] = useState(true);
  const [hideModal, setHideModal] = useState(false);

  useEffect(() => {
    ensureEditData();
    ensureBankCodes();
    ensureBankAccountTypes();
  }, []);

  const ensureEditData = () => {
    const agency = state?.agency;
    const account = state?.account;
    const account_check_digit = state?.account_check_digit;

    setBankFormData((prevState) => {
      const newState = { ...prevState };

      if (agency) {
        Object.assign(newState, { agency });
      }

      if (account) {
        Object.assign(newState, {
          account: account_check_digit
            ? account + account_check_digit
            : account,
        });
      }

      return newState;
    });
  };

  const ensureBankCodes = async () => {
    try {
      const response = (await api.get('/bank-codes?limit=500')) as IResponse;

      const supportedCodeBanks = [
        '237',
        '001',
        '341',
        '260',
        '197',
        '104',
        '033',
        '077',
        '336',
        '208',
        '422',
      ];

      const filteredBanks = response?.data?.results.filter((bank) =>
        supportedCodeBanks.includes(bank.code),
      );

      const options = filteredBanks.map((bank) => ({
        label: bank.name,
        value: bank.id,
      }));

      setBankNameOptions(options);

      const bank_code_id = state?.bank_code_id;
      if (bank_code_id) {
        const bank = options?.find(
          (bank) => bank.value.toString() === bank_code_id,
        );

        if (bank) {
          setBankName(bank);
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const ensureBankAccountTypes = async () => {
    try {
      const response = (await api.get('/bank-account-types')) as IResponse;

      const options = response?.data?.results.map((account) => ({
        label: account.name,
        value: account.id,
      }));

      setAccountTypeOptions(options);

      const bank_account_type_id = state?.bank_account_type_id;

      if (bank_account_type_id) {
        const account = options?.find(
          (account) => account.value.toString() === bank_account_type_id,
        );

        if (account) {
          setAccountType(account);
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    handleFormValidate();
  }, [bankName, accountType, bankFormData]);

  const handleFormValidate = () => {
    const bankNameEntry = +!!bankName?.value;
    const accountTypeEntry = +!!accountType?.value;

    const formDataEntries =
      Object.values(bankFormData).filter((value) => value !== '').length +
      accountTypeEntry +
      bankNameEntry;

    if (formDataEntries >= 4) {
      setDisableButton(false);
    } else if (!disableButton) {
      setDisableButton(true);
    }
  };

  const handleDataSave = () => {
    const accountData =
      bankFormData?.account?.split('-') || bankFormData?.account;

    const accountDigit =
      accountData?.length > 1
        ? accountData[1]
        : bankFormData?.account[bankFormData?.account?.length - 1];

    const accountNumber =
      accountData?.length > 1
        ? accountData[0]
        : accountData[0]?.substr(0, bankFormData?.account?.length - 1);

    setState((prevState) => ({
      ...prevState,
      agency: bankFormData?.agency,
      account: accountNumber,
      legal_name: user?.individual_person?.name || '',
      document_number: user?.individual_person?.cpf || '',
      account_check_digit: accountDigit,
      bank_code_id: bankName?.value.toString(),
      bank_account_type_id: accountType?.value.toString(),
    }));

    handleCloseModal();
  };

  const handleCloseModal = () => {
    setHideModal((prevState) => !prevState);

    setTimeout(() => {
      toggleModal((prevState) => !prevState);

      if (callback) {
        callback();
      }
    }, 500);
  };

  useOutside(modalRef, handleCloseModal);

  if (!modalRoot) {
    return null;
  }

  return ReactDOM.createPortal(
    <ModalSection className={`${hideModal ? 'modal-bank-data--hide' : ''}`}>
      <ModalContainer ref={modalRef}>
        <ModalCloseButton type="button" onClick={handleCloseModal}>
          X
        </ModalCloseButton>
        <ModalTitle>Insira seus dados bancários</ModalTitle>
        <SelectWrapper>
          <RadialSelectBox
            placeholder="Banco"
            selectedItem={bankName}
            setSelectedItem={setBankName}
            options={bankNameOptions}
          />
        </SelectWrapper>
        <SelectWrapper>
          <RadialSelectBox
            placeholder="Tipo de Conta"
            selectedItem={accountType}
            setSelectedItem={setAccountType}
            options={accountTypeOptions}
          />
        </SelectWrapper>
        <ModalInput
          type="text"
          placeholder="Agência"
          name="agency"
          value={bankFormData?.agency}
          onChange={(event) =>
            HandleInputChange({ event, setState: setBankFormData })
          }
        />
        <ModalInput
          type="text"
          placeholder="Conta e Dígito"
          name="account"
          value={bankFormData?.account}
          onChange={(event) =>
            HandleInputChange({ event, setState: setBankFormData })
          }
        />
        <ModalButton
          type="button"
          onClick={handleDataSave}
          disabled={disableButton}
        >
          Confirmar
        </ModalButton>
      </ModalContainer>
    </ModalSection>,
    modalRoot,
  );
};
